import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  awaitingDraftMaterializationAtom,
  draftRebuildFailureAtom,
  draftRebuildsEndedAtom,
  draftReloadAtom,
  draftTablesReloadAtom,
  flipDraftMapSelectionAtom,
  updateDraftAssetsAtom,
  updatePackageDraftAtom,
} from './atoms';
import type {
  DraftAssetsUpdatePayload,
  DraftAssetsUpdateResponse,
  DraftRebuildFailure,
  LargeTableWarning,
  PackageFormInstance,
  PackageWizardApi,
  ScopePayload,
} from './types';
import {
  DraftMaterializationTimeout,
  DraftRefusal,
  LargeTableRefusal,
  draftPusherChannel,
  markDraftTouched,
  subscribeForDraftMaterialization,
  type DraftMaterializationWatcher,
} from './utils';

/**
 * `api.upsertDraft` with the large-table warning. Above the warning size the
 * server builds the Assets table only once the user agrees: it refuses, writing
 * nothing, so `sync` asks (`warning` holds the modal's props while it does) and,
 * on Continue, sends the same scope again confirmed. On Cancel, or when Select
 * Source goes while the warning is open, it throws the refusal, which leaves
 * Select Source as it was.
 *
 * @param {PackageWizardApi} wizardApi - The wizard's api
 * @return {object} `sync(scope)` and the open warning, or null
 */
export const useLargeTableConfirmedSync = (wizardApi: PackageWizardApi) => {
  const [warning, setWarning] = useState<LargeTableWarning | null>(null);
  // The open warning's answer, so an unmount can answer Cancel.
  const answerRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => () => answerRef.current?.(false), []);

  const sync = useCallback(
    async (scope: ScopePayload) => {
      try {
        await wizardApi.upsertDraft(scope);
      } catch (error) {
        if (!(error instanceof LargeTableRefusal)) throw error;
        const build = await new Promise<boolean>((resolve) => {
          /**
           * Closes the warning with the user's answer.
           *
           * @param {boolean} value - True to build the table
           */
          const answer = (value: boolean) => {
            answerRef.current = null;
            setWarning(null);
            resolve(value);
          };
          answerRef.current = answer;
          setWarning({
            assetCount: error.tableAssetCount,
            onContinue: () => answer(true),
            onCancel: () => answer(false),
          });
        });
        if (!build) throw error;
        await wizardApi.upsertDraft(scope, { confirmLargeTable: true });
      }
    },
    [wizardApi]
  );

  return { sync, warning };
};

/**
 * Step 1's one wait for a build an asset change queued. It holds the whole
 * step (`awaitingDraftMaterializationAtom`) until the build reports back, keeps
 * how it ended when not ready (`draftRebuildFailureAtom`; Try again waits on the
 * same draft again, as a re-Next after a timeout does), and then has the step
 * re-read the draft (`draftReloadAtom`), counting the build as ended
 * (`draftRebuildsEndedAtom`) unless the wait ran out first. It never throws.
 *
 * @return {Function} Waits on `watcher`, then cancels it
 */
export const useDraftRebuild = () => {
  const setAwaiting = useSetAtom(awaitingDraftMaterializationAtom);
  const setFailure = useSetAtom(draftRebuildFailureAtom);
  const reload = useSetAtom(draftReloadAtom);
  const countEnded = useSetAtom(draftRebuildsEndedAtom);

  return useCallback(
    async (watcher: DraftMaterializationWatcher): Promise<void> => {
      setAwaiting(true);
      setFailure(null);
      // A wait that times out has not seen the build end: Try again waits for that.
      let ended = true;
      try {
        await watcher.wait();
      } catch (error) {
        ended = !(error instanceof DraftMaterializationTimeout);
        setFailure({
          message: error instanceof Error ? error.message : String(error),
          timedOut: !ended,
        });
      } finally {
        watcher.cancel();
        setAwaiting(false);
        reload((count) => count + 1);
        if (ended) countEnded((count) => count + 1);
      }
    },
    [setAwaiting, setFailure, reload, countEnded]
  );
};

/**
 * Try again after Step 1's wait for a build did not end ready, waiting through
 * `useDraftRebuild`. After a timeout it waits on the same draft again, as a
 * re-Next does, rather than sending the change again. After a failed build the
 * server builds the draft again as it stands (`retryBuild`: its scope, ticks
 * and scenario), and the step waits for that build. A retry refused with
 * reasons (most often a draft no longer failed: retried or switched in another
 * tab, say) waits on the draft as it stands: a ready draft ends the wait at
 * once, and one still failed shows its failure again.
 *
 * @param {string | null} draftId - The draft; none, nothing to try
 * @return {Function} Tries again after `failure`
 */
export const useDraftRebuildTryAgain = (draftId: string | null) => {
  const awaitRebuild = useDraftRebuild();
  const updateDraft = useSetAtom(updatePackageDraftAtom);

  return useCallback(
    ({ timedOut }: DraftRebuildFailure) => {
      if (draftId === null) return;
      const watcher = subscribeForDraftMaterialization(
        draftPusherChannel(draftId),
        { draftId }
      );
      if (timedOut) {
        void awaitRebuild(watcher);
        return;
      }
      void awaitRebuild({
        // Waited on once the draft is building again, so its status checks do
        // not read the failed build's status.
        wait: async () => {
          try {
            await updateDraft({ draftId, payload: { retryBuild: true } });
          } catch (error) {
            if (!(error instanceof DraftRefusal)) throw error;
          }
          await watcher.wait();
        },
        cancel: () => watcher.cancel(),
      });
    },
    [awaitRebuild, draftId, updateDraft]
  );
};

/**
 * Sends one change of the draft's assets (`PATCH /packages/drafts/:id/assets`)
 * the way every Step 1 control does. A tick answers 200 and nothing waits; a
 * change that queues a build answers 202 and goes through `useDraftRebuild`,
 * the step held until the build is done. Either way the step then re-reads the
 * draft, so it shows what the server holds; a tick of whole assets is drawn on
 * the map at once while that re-read brings their wells and development areas.
 * After a refused change only the tables read again: nothing changed. A
 * refused change throws its error, for the caller to show.
 *
 * @param {PackageFormInstance} form - The wizard form, marked touched by a change
 * @return {Function} Sends a change; resolves with its answer once settled
 */
export const useDraftAssetChange = (form: PackageFormInstance) => {
  const updateDraftAssets = useSetAtom(updateDraftAssetsAtom);
  const reload = useSetAtom(draftReloadAtom);
  const reloadTables = useSetAtom(draftTablesReloadAtom);
  const flipMapSelection = useSetAtom(flipDraftMapSelectionAtom);
  const awaitRebuild = useDraftRebuild();

  return useCallback(
    async (
      payload: DraftAssetsUpdatePayload
    ): Promise<DraftAssetsUpdateResponse> => {
      const { draftId } = payload;
      // Subscribed before the PATCH, so a build that reports back at once is
      // not missed; the draft id arms the watcher's status checks.
      const watcher = subscribeForDraftMaterialization(
        draftPusherChannel(draftId),
        { draftId }
      );
      let result: DraftAssetsUpdateResponse;
      try {
        result = await updateDraftAssets(payload);
      } catch (error) {
        watcher.cancel();
        reloadTables((count) => count + 1);
        throw error;
      }
      markDraftTouched(form);
      if (result.addedRowCount === null) {
        await awaitRebuild(watcher);
      } else {
        watcher.cancel();
        // A tick of whole assets draws their Tracts at once (select all,
        // deselect all and a Subtract tick can change any Tract the map draws);
        // the re-read brings their wells and development areas.
        if (
          !('selectAll' in payload) &&
          !payload.addedSubtractIds?.length &&
          !payload.removedSubtractIds?.length
        ) {
          flipMapSelection(payload);
        }
        reload((count) => count + 1);
      }
      return result;
    },
    [
      form,
      updateDraftAssets,
      reload,
      reloadTables,
      flipMapSelection,
      awaitRebuild,
    ]
  );
};
